import { createApp, watch } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { useUsuariosStore } from './stores/usuarios'
import { hayEdicionSinGuardar } from './composables/edicionPendiente'

import "@/firebase"

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)

// Inicializar ANTES de montar la app y el router
const usuariosStore = useUsuariosStore()
usuariosStore.inicializarAuthListener()

app.use(router)
app.mount('#app')

// ────────────────────────────────────────────────────────────────────────────
// Service Worker: actualización silenciosa en segundo plano (Opción C)
//
// El service worker nuevo se instala y se activa solo (skipWaiting + clientsClaim,
// configurados en vue.config.js). NO mostramos ningún cartel y NO forzamos
// recargas: la versión nueva se aplica de forma natural la próxima vez que el
// usuario abra o recargue la app. Así nadie pierde un formulario a medio cargar.
//
// IMPORTANTE: esto solo afecta a los archivos estáticos de la app (JS/CSS/
// imágenes). NO toca datos de los usuarios: historias clínicas, presupuestos,
// turnos, pacientes y fotos viven en Firebase (Firestore/Storage), no en la
// caché del navegador.
// ────────────────────────────────────────────────────────────────────────────
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker.js')

  // ────────────────────────────────────────────────────────────────────────────
  // Auto-actualización al volver a la app.
  //
  // Caso clave: iPhone con la app instalada en la pantalla de inicio. Ahí NO hay
  // botón de "recargar" del navegador, así que la versión vieja quedaba pegada y
  // la doctora no veía las novedades. Solución: cada vez que la app vuelve al
  // frente, le pedimos al navegador que busque una versión nueva. Si la encuentra,
  // el service worker nuevo toma el control y recargamos UNA sola vez para mostrarla.
  //
  // Sigue siendo silencioso (sin cartel ni modal). La diferencia con antes es que
  // ahora SÍ recarga sola al traer la app al frente cuando hay versión nueva.
  //
  // SEGURIDAD: NUNCA recarga mientras se está escribiendo algo sin guardar (ver la
  // banderita `hayEdicionSinGuardar`). Si justo hay una evolución a medias, la recarga
  // queda pendiente y se aplica recién cuando se termina y guarda. Así no se pierde
  // ningún texto, ni siquiera en iPhone (donde el cartel del navegador no se dispara).
  //
  // El guard `controller` evita recargar en la primera visita (cuando el SW toma
  // control por primera vez, que no es una actualización).
  // ────────────────────────────────────────────────────────────────────────────
  let recargandoPorActualizacion = false
  let actualizacionPendiente = false

  const recargarSiEsSeguro = () => {
    if (recargandoPorActualizacion) return
    // Si se está escribiendo algo sin guardar, NO recargar: dejar pendiente.
    if (hayEdicionSinGuardar.value) {
      actualizacionPendiente = true
      return
    }
    recargandoPorActualizacion = true
    window.location.reload()
  }

  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener('controllerchange', recargarSiEsSeguro)
  }

  // Cuando se deja de editar (se guardó o se cerró el formulario), si había una
  // actualización esperando, recién ahí se aplica.
  watch(hayEdicionSinGuardar, (hay) => {
    if (!hay && actualizacionPendiente) recargarSiEsSeguro()
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    navigator.serviceWorker.getRegistration()
      .then((registration) => registration && registration.update())
      .catch(() => {})
  })
}

// Red de seguridad para el error "Loading chunk failed".
// Si un usuario tiene una pestaña vieja abierta y, al navegar a una sección,
// el navegador no encuentra el archivo de esa sección porque se actualizó en
// segundo plano, recargamos UNA sola vez para tomar la versión nueva.
// Solo se dispara cuando una sección NO pudo cargar (la navegación ya venía
// fallando); no interrumpe a quien está usando una pantalla que ya cargó bien.
function recargarSiArchivoDesactualizado(mensaje) {
  const esErrorDeChunk =
    /Loading chunk\s+\S+\s+failed/i.test(mensaje) ||
    /Loading CSS chunk\s+\S+\s+failed/i.test(mensaje) ||
    /ChunkLoadError/i.test(mensaje)
  if (!esErrorDeChunk) return

  const ultimaRecarga = Number(sessionStorage.getItem('recarga-chunk-ts') || 0)
  // Si ya recargamos hace menos de 10 segundos, no repetimos (evita bucles).
  if (Date.now() - ultimaRecarga < 10000) return
  sessionStorage.setItem('recarga-chunk-ts', String(Date.now()))
  window.location.reload()
}

window.addEventListener('error', (event) => {
  recargarSiArchivoDesactualizado(event && event.message ? event.message : '')
})
window.addEventListener('unhandledrejection', (event) => {
  const mensaje = event && event.reason && event.reason.message ? event.reason.message : ''
  recargarSiArchivoDesactualizado(mensaje)
})
